import React, { useContext, useState } from "react"
import { Link } from "react-router-dom"
import DOMPurify from "dompurify"
import { SmartDateFormat } from "../../util/dates"
import CommentVoteBlock from "../comments/CommentVoteBlock"
import UserAvatar from "../shared/Header/UserAvatar"
import LinkifyText from "../shared/LinkifyText"
import { Comment, CommentTreeNode } from "../../requests/comments"
import { mutationRequest, request } from "../../util/request"
import clsx from "clsx"
import CommentForm from "../comments/CommentForm"
import { UserContext } from "../HomeApp"
import CommentMedia from "./CommentMedia"

const renderCommentHtml = (text: string): string => {
  const linked = LinkifyText(text)
  const bolded = linked.replace(/@([^:<]+):/, "<strong>@$1:</strong>")
  return DOMPurify.sanitize(bolded, {
    ALLOWED_TAGS: ["span", "a", "iframe", "video", "source", "img", "strong"],
    ALLOWED_ATTR: [
      "onerror",
      "class",
      "src",
      "controls",
      "poster",
      "href",
      "target",
      "style",
    ],
  })
}

interface CommentBlockProps {
  comment: Comment | CommentTreeNode
  depth?: number
  isTreeNode?: boolean
  postId?: number
  postPath?: string | null
  highlighted?: boolean
  onReplyAdded?: (reply: Comment, parentId: number) => void
  // CommentSectionPreview covers its comment list with a "go to full post"
  // click-through overlay (pointer-events-none list under an absolute,
  // higher z-index link) - this punches a hole back through that overlay so
  // voting still works without navigating away. Unused/no-op outside that
  // preview context, where there's no overlay to escape in the first place.
  interactiveVoteBlock?: boolean
}

const CommentBlock: React.FC<CommentBlockProps> = ({
  comment,
  depth = 0,
  isTreeNode = false,
  postId,
  postPath = null,
  highlighted = false,
  onReplyAdded,
  interactiveVoteBlock = false,
}) => {
  const { user } = useContext(UserContext)
  const [deleted, setDeleted] = useState(false)
  const node = isTreeNode ? (comment as CommentTreeNode) : null
  // baseReplies comes directly from the live query data and updates on every refetch
  const baseReplies = node?.replies ?? []
  const hasMoreReplies = node?.has_more_replies ?? false
  const repliesCount = node?.replies_count ?? 0
  // extraReplies holds any additional replies fetched via "load more"
  const [extraReplies, setExtraReplies] = useState<CommentTreeNode[]>([])
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [showReplyForm, setShowReplyForm] = useState(false)

  // Merge base and extra, deduplicating so a refetch never causes duplicates
  const baseIds = new Set(baseReplies.map((r) => r.id))
  const loadedReplies = [
    ...baseReplies,
    ...extraReplies.filter((r) => !baseIds.has(r.id)),
  ]

  const isFeatured = comment.comment_type === "premiere_blurb"
  const indentStyle = depth > 0 ? { marginLeft: depth * 20 } : undefined
  const borderLeftClass = depth > 0 ? "border-l-2 border-gray-300 pl-4" : ""

  const handleLoadMoreReplies = async () => {
    if (!postId) return
    setIsLoadingMore(true)
    try {
      const json = await request({
        queryKey: [
          "comments",
          {
            path: `/api/v1/posts/${postId}/comments.json`,
            params: { parent_id: comment.id },
          },
        ],
      })
      if (json.replies) {
        // Only add replies not already in the combined list
        const existingIds = new Set(loadedReplies.map((r) => r.id))
        setExtraReplies((prev) => [
          ...prev,
          ...json.replies.filter(
            (r: CommentTreeNode) => !existingIds.has(r.id),
          ),
        ])
      }
    } finally {
      setIsLoadingMore(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete your comment?")) return
    await mutationRequest({
      path: `/api/v1/posts/${comment.post_id}/comments/${comment.id}`,
      options: { method: "DELETE" },
    })
    setDeleted(true)
  }

  if (deleted) return null

  return (
    <>
      <li
        style={indentStyle}
        className={clsx([
          "text-sm",
          "px-2",
          "py-1",
          "rounded-sm",
          "mb-4",
          borderLeftClass,
          isFeatured ? "border-4 border-bn-premiere-green bg-black-800" : "",
        ])}
      >
        <span className="flex flex-row">
          <UserAvatar
            handleClick={() => {}}
            user={{
              id: comment.user_id,
              avatar_url_small: comment.user_avatar_url,
            }}
            size="small"
            inHeader={false}
          />
          <div className="px-4 w-full">
            <div>
              <span
                className={clsx([
                  "font-bold",
                  "mr-3",
                  isFeatured ? "text-white" : "text-gray-900",
                ])}
              >
                {comment.user_path ? (
                  <Link to={comment.user_path}>{comment.username}</Link>
                ) : (
                  comment.username
                )}
              </span>
              <span
                className={clsx([
                  "text-xs",
                  isFeatured ? "text-white" : "text-gray-700",
                ])}
              >
                {SmartDateFormat({
                  dateStr: comment.created_at,
                  includeTime: true,
                })}
              </span>
            </div>
            {isFeatured && (
              <div className="leading-none mb-1 text-bn-premiere-green text-xs italic">
                nadaLight Feature
              </div>
            )}
            <div
              className={isFeatured ? "text-white" : "text-black"}
              dangerouslySetInnerHTML={{
                __html: renderCommentHtml(comment.comment_text),
              }}
            />
            {comment.media_type === "direct_upload" &&
              (comment.attached_media && comment.attached_media.length > 0 ? (
                <CommentMedia
                  media={comment.attached_media}
                  altText={comment.comment_text}
                />
              ) : (
                comment.media_urls &&
                comment.media_urls.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {comment.media_urls.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer">
                        <img
                          style={{ maxHeight: "250px" }}
                          src={url}
                          alt={comment.comment_text}
                          className="object-contain"
                        />
                      </a>
                    ))}
                  </div>
                )
              ))}
          </div>
        </span>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <div className={interactiveVoteBlock ? "relative z-20 pointer-events-auto" : undefined}>
            <CommentVoteBlock contentId={comment.id} postId={comment.post_id} />
          </div>
          {isTreeNode &&
            (postPath ? (
              <a
                href={postPath}
                className={clsx("text-xs hover:underline", isFeatured ? "text-bn-premiere-green" : "text-blue-500")}
              >
                Reply
              </a>
            ) : (
              <button
                type="button"
                className={clsx("text-xs hover:underline focus:outline-none", isFeatured ? "text-bn-premiere-green" : "text-blue-500")}
                onClick={() => setShowReplyForm((v) => !v)}
              >
                {showReplyForm ? "Cancel" : "Reply"}
              </button>
            ))}
          {user?.id === comment.user_id && (
            <button
              type="button"
              className="text-red-400 text-xs hover:underline focus:outline-none"
              onClick={handleDelete}
            >
              Delete
            </button>
          )}
        </div>

        {isTreeNode && showReplyForm && postId && !isFeatured && (
          <div className="mt-2">
            <CommentForm
              postId={postId}
              parentId={comment.id}
              parentUsername={comment.username}
              onSuccess={(newReply) => {
                setShowReplyForm(false)
                if (onReplyAdded && newReply) onReplyAdded(newReply, comment.id)
              }}
              onCancel={() => setShowReplyForm(false)}
            />
          </div>
        )}

        {isTreeNode && !postPath && loadedReplies.length > 0 && !isFeatured && (
          <ul className="mt-2">
            {loadedReplies.map((replyNode) => (
              <CommentBlock
                key={`comment-${replyNode.id}`}
                comment={replyNode}
                depth={depth + 1}
                isTreeNode
                postId={postId}
                postPath={postPath}
                onReplyAdded={onReplyAdded}
              />
            ))}
          </ul>
        )}

        {isTreeNode && !postPath && hasMoreReplies && postId && !isFeatured && (
          <button
            type="button"
            className="mt-2 text-blue-500 text-xs hover:underline focus:outline-none"
            onClick={handleLoadMoreReplies}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? "Loading..." : `Load more replies (${repliesCount})`}
          </button>
        )}
      </li>

      {isFeatured && isTreeNode && postId && (
        <>
          {showReplyForm && (
            <div className="mt-2">
              <CommentForm
                postId={postId}
                parentId={comment.id}
                parentUsername={comment.username}
                onSuccess={(newReply) => {
                  setShowReplyForm(false)
                  if (onReplyAdded && newReply) onReplyAdded(newReply, comment.id)
                }}
                onCancel={() => setShowReplyForm(false)}
              />
            </div>
          )}
          {!postPath && loadedReplies.length > 0 && (
            <ul className="mt-2">
              {loadedReplies.map((replyNode) => (
                <CommentBlock
                  key={`comment-${replyNode.id}`}
                  comment={replyNode}
                  depth={depth + 1}
                  isTreeNode
                  postId={postId}
                  postPath={postPath}
                  onReplyAdded={onReplyAdded}
                />
              ))}
            </ul>
          )}
          {!postPath && hasMoreReplies && (
            <button
              type="button"
              className="mt-2 text-blue-500 text-xs hover:underline focus:outline-none"
              onClick={handleLoadMoreReplies}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? "Loading..." : `Load more replies (${repliesCount})`}
            </button>
          )}
        </>
      )}
    </>
  )
}

export default CommentBlock
